import { GridRenderCellParams } from "@mui/x-data-grid";
import { Chip, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { messages } from "../../../locales/messages";
import { AllowedTableColumn } from "../../../utils/types/AllowTableTypes";
import { RenderEditButton } from "../../components/RenderEditButtonTable";
import { RenderDeleteButton } from "../../components/RenderDeleteButtonTable";
import { RenderMultiImageTable } from "../../components/RenderImageTable";
import { PermissionComponentWrapper } from "../../components/PermissionComponentWrapper";
import { routes } from "../../../utils/constant/navigationRoutes";
import {
  BannerDisplayState,
  getBannerDisplayState,
} from "../../../utils/banner/bannerFields";
import { BannerRow, toBannerDate } from "../../../utils/banner/bannerRow";
import { formatDateInEasternTime } from "../../../utils/dateUtil/formatDateInEasternTime";

interface BannerTableColumnsProps {
  handleOnDelete: (value: BannerRow) => void;
}

const STATE_COLOR: Record<BannerDisplayState, string> = {
  draft: "#9e9e9e",
  paused: "#9e9e9e",
  archived: "#bdbdbd",
  scheduled: "#f58220",
  expired: "#d32f2f",
  live: "#2e7d32",
};

export const useBannerTableColumns = ({
  handleOnDelete,
}: BannerTableColumnsProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dateColumns: AllowedTableColumn[] = ["updatedAt", "createdAt"];

  const stateLabel: Record<BannerDisplayState, string> = {
    draft: t(messages.banner_state_draft()),
    paused: t(messages.banner_state_paused()),
    archived: t(messages.banner_state_archived()),
    scheduled: t(messages.banner_state_scheduled()),
    expired: t(messages.banner_state_expired()),
    live: t(messages.banner_state_live()),
  };

  const handleOnClickEdit = (value: BannerRow) => {
    navigate(routes.banner.editById(value?.id || ""));
  };

  const columns = [
    {
      field: "edit",
      width: 90,
      headerName: `${t(messages.Edit())}`,
      renderCell: (props: GridRenderCellParams) => (
        <PermissionComponentWrapper
          requiredPermission={["edit"]}
          requiredPermissionGroup={["banner"]}
        >
          <RenderEditButton onClick={handleOnClickEdit} {...props} />
        </PermissionComponentWrapper>
      ),
      sortable: false,
    },
    {
      field: "state",
      width: 120,
      headerName: `${t(messages.banner_state())}`,
      sortable: false,
      renderCell: (props: GridRenderCellParams<BannerRow>) => {
        const state = getBannerDisplayState(props.row);
        return (
          <Chip
            size="small"
            label={stateLabel[state]}
            sx={{
              backgroundColor: STATE_COLOR[state],
              color: "#fff",
              fontWeight: 600,
            }}
          />
        );
      },
    },
    {
      field: "name",
      width: 260,
      headerName: `${t(messages.banner_name())}`,
    },
    {
      field: "placement",
      width: 180,
      headerName: `${t(messages.banner_placement())}`,
    },
    {
      field: "status",
      width: 110,
      headerName: `${t(messages.banner_status())}`,
    },
    {
      field: "priority",
      width: 100,
      headerName: `${t(messages.banner_priority())}`,
      type: "number",
    },
    {
      field: "desktopImage",
      width: 110,
      headerName: `${t(messages.banner_desktop_image())}`,
      sortable: false,
      renderCell: (props: GridRenderCellParams<BannerRow>) => {
        const image = props.row.desktopImage;
        return <RenderMultiImageTable images={image ? [image] : []} />;
      },
    },
    {
      field: "validity",
      width: 220,
      headerName: `${t(messages.banner_validity())}`,
      sortable: false,
      valueGetter: (props: GridRenderCellParams<BannerRow>) => {
        const start = toBannerDate(props.row.validStartDate);
        const end = toBannerDate(props.row.validEndDate);
        if (!start && !end) return "—";
        const startLabel = start
          ? formatDateInEasternTime(start, "MMM D, YYYY")
          : "—";
        const endLabel = end
          ? formatDateInEasternTime(end, "MMM D, YYYY")
          : "—";
        return `${startLabel} → ${endLabel}`;
      },
    },
    {
      field: "sponsored",
      width: 110,
      headerName: `${t(messages.banner_sponsored())}`,
      type: "boolean",
    },
    {
      field: "advertiserName",
      width: 160,
      headerName: `${t(messages.banner_advertiser_name())}`,
    },
    {
      field: "createdByAdmin",
      width: 160,
      headerName: `${t(messages.banner_created_by())}`,
      sortable: false,
      valueGetter: (props: GridRenderCellParams<BannerRow>) =>
        props.row.createdByAdmin?.userName ?? "—",
    },
    {
      field: "targeting",
      width: 110,
      headerName: `${t(messages.banner_section_targeting())}`,
      sortable: false,
      renderCell: (props: GridRenderCellParams<BannerRow>) => {
        if (!props.row.targetCenter) return <span>—</span>;
        const [longitude, latitude] = props.row.targetCenter.coordinates ?? [];
        return (
          <Tooltip
            title={`${latitude}, ${longitude} · ${props.row.targetRadiusKm ?? "?"} km`}
          >
            <Chip
              size="small"
              label={`${props.row.targetRadiusKm ?? "?"} km`}
            />
          </Tooltip>
        );
      },
    },
    ...dateColumns.map((value) => ({
      width: 200,
      field: value,
      headerName: `${t(messages[value]())}`,
      type: "dateTime",
      valueGetter: (val: GridRenderCellParams) =>
        val?.row?.[value] ? new Date(val.row[value]) : undefined,
    })),
    {
      field: "id",
      width: 300,
      headerName: `${t(messages["id"]())}`,
    },
    {
      field: "delete",
      width: 90,
      headerName: `${t(messages.Delete())}`,
      renderCell: (props: GridRenderCellParams) => (
        <PermissionComponentWrapper
          requiredPermission={["delete"]}
          requiredPermissionGroup={["banner"]}
        >
          <RenderDeleteButton
            onClick={() => handleOnDelete(props.row as BannerRow)}
            {...props}
          />
        </PermissionComponentWrapper>
      ),
      sortable: false,
    },
  ];

  return columns;
};
